import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import './PrimaryCampersContact.css';

interface PrimaryCamperRecord {
  id: string;
  firstName: string;
  lastName: string;
  homePhone: string;
  email: string;
  street: string;
  town: string;
  province: string;
  postalCode: string;
  lastCampYear: string;
}

const PrimaryCampersContact: React.FC = () => {
  const [campers, setCampers] = useState<PrimaryCamperRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof PrimaryCamperRecord>('lastName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchCampers();
  }, []);

  const fetchCampers = async () => {
    try {
      setLoading(true);
      const campersRef = collection(db, 'primaryCampers');
      const campersSnapshot = await getDocs(campersRef);
      
      const camperRecords: PrimaryCamperRecord[] = campersSnapshot.docs
        .filter(doc => !doc.data().deleted)
        .map(doc => {
          const data = doc.data();
          
          // Get most recent camp year from accommodationHistory[0]
          let lastCampYear = '';
          const recentHistory = data.accommodationHistory?.[0];
          if (recentHistory) {
            if (recentHistory.placeCode === 'No History') {
              lastCampYear = 'No History';
            } else {
              lastCampYear = String(recentHistory.year || '');
            }
          }
          
          return {
            id: doc.id,
            firstName: data.FirstName || '',
            lastName: data.LastName || '',
            homePhone: data.HomePhone || '',
            email: data.email || '',
            street: data.Street || '',
            town: data.Town || '',
            province: data.Province || '',
            postalCode: data.PostalCode || '',
            lastCampYear
          };
        });

      setCampers(camperRecords);
    } catch (err) {
      console.error('Error fetching primary campers:', err);
      setError('Error fetching primary campers: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: keyof PrimaryCamperRecord) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedCampers = [...campers].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return 0;
  });

  const exportToCSV = () => {
    const headers = ['First Name', 'Last Name', 'Phone', 'Email', 'Street', 'Town', 'Province', 'Postal Code', 'Last Camp Year'];
    const csvContent = [
      headers.join(','),
      ...sortedCampers.map(record => [
        `"${record.firstName}"`,
        `"${record.lastName}"`,
        `"${record.homePhone}"`,
        `"${record.email}"`,
        `"${record.street}"`,
        `"${record.town}"`,
        `"${record.province}"`,
        `"${record.postalCode}"`,
        `"${record.lastCampYear}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `primary-campers-contact-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  if (loading) {
    return <div className="campers-contact-loading">Loading primary campers...</div>;
  }

  if (error) {
    return <div className="campers-contact-error">{error}</div>;
  }

  return (
    <div className="campers-contact-report">
      <div className="campers-contact-header">
        <h2>Primary Campers Contact List</h2>
        <div className="campers-contact-actions">
          <button onClick={printReport} className="print-button">
            Print Report
          </button>
          <button onClick={exportToCSV} className="export-button">
            Export to CSV
          </button>
        </div>
      </div>

      <div className="campers-contact-summary">
        <p>Total Records: {campers.length}</p>
      </div>

      <div className="campers-contact-table-container">
        <table className="campers-contact-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('firstName')} className="sortable">
                First Name {sortField === 'firstName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('lastName')} className="sortable">
                Last Name {sortField === 'lastName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('homePhone')} className="sortable">
                Phone {sortField === 'homePhone' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('email')} className="sortable">
                Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('street')} className="sortable">
                Street {sortField === 'street' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('town')} className="sortable">
                Town {sortField === 'town' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('province')} className="sortable">
                Province {sortField === 'province' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('postalCode')} className="sortable">
                Postal Code {sortField === 'postalCode' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('lastCampYear')} className="sortable">
                Last Camp Year {sortField === 'lastCampYear' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedCampers.map((record) => (
              <tr key={record.id}>
                <td>{record.firstName}</td>
                <td>{record.lastName}</td>
                <td>{record.homePhone}</td>
                <td>{record.email}</td>
                <td>{record.street}</td>
                <td>{record.town}</td>
                <td>{record.province}</td>
                <td>{record.postalCode}</td>
                <td>{record.lastCampYear}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {campers.length === 0 && (
        <div className="no-campers">
          <p>No primary campers found.</p>
        </div>
      )}
    </div>
  );
};

export default PrimaryCampersContact;
